import React from 'react'
import cx from 'classnames'
import { observer, useObserver } from 'mobx-react'
import {
  InfoSectionItems,
  InfoSectionItem,
} from 'admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/InfoSection'
import WorkspaceApplicant, {
  WorkspaceApplicantStatus,
} from 'admin/staffing/WorkspaceApplicant'
import WorkspacePosting, {
  WorkspacePostingStatus,
} from 'admin/staffing/WorkspacePosting'
import Button from 'components/Button'
// import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import {
  ImageCell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TextCell,
} from 'components/Table'
import { capitalizeFirstLetter } from 'utils/string-util'
import StaffingDetailsViewModel from './StaffingDetailsViewModel'
import styles from './StaffingDetailsPanel.sass'

interface StaffingDetailsPanelProps {
  vm: StaffingDetailsViewModel
}

export default observer(({ vm }: StaffingDetailsPanelProps) => {
  return vm.showing ? (
    <div className={styles.root}>
      <StaffingDetailsHeader vm={vm} />
      <StaffingDetailsInfoSection vm={vm} />
      <StaffingDetailsApplicantsSection vm={vm} />
      {vm.showActions && <StaffingDetailsActionFooter vm={vm} />}
    </div>
  ) : null
})

const StaffingDetailsHeader = observer(function StaffingDetailsHeader({
  vm,
}: StaffingDetailsPanelProps) {
  return (
    <div className={styles.header}>
      <div className={styles.mainContainer}>
        <IconButton onClick={vm.close}>
          <Icon color="var(--grey600)" name="back" size={24} />
        </IconButton>
      </div>
      {/* TODO: Enable when invite pro modal is set up. */}
      {/* <div className={styles.extrasContainer}>
      <Dropdown
        autoDismiss
        width="miniscule"
        position="bottom-end"
        button={
          <Button
            size="baseline"
            type="text"
            dataTest={`applicant-manager-sidebar-open-${vm.workspacePosting?.id}`}
          >
            <Icon color="var(--grey600)" name="more" />
          </Button>
        }
      >
        <div>
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onClick={undefined}
            disabled={undefined}
            dataTest={`applicant-manager-sidebar-invite-pro-${vm.workspacePosting?.id}`}
          >
            Invite Pro
          </DropdownItem>
          <DropdownItem
            onClick={undefined}
            disabled={undefined}
            dataTest={`applicant-manager-sidebar-duplicate-${vm.workspacePosting?.id}`}
          >
            Duplicate
          </DropdownItem>
        </div>
      </Dropdown>
      </div> */}
    </div>
  )
})

const StaffingDetailsInfoSection = observer(
  function StaffingDetailsInfoSection({ vm }: StaffingDetailsPanelProps) {
    const applicantsContracted = !vm.applicantsExpanded
    const posting = vm.workspacePosting

    if (!posting) {
      return null
    }

    return (
      <div
        className={cx(
          styles.info,
          applicantsContracted && styles.applicantsContracted
        )}
      >
        <h2 className={styles.infoTitle}>{posting.workspace.name}</h2>
        <InfoSectionItems>
          <InfoSectionItem label="Location">{posting.location}</InfoSectionItem>
          <InfoSectionItem label="Primary software">
            {posting.primarySoftware}
            {posting.backupFileNeeded && (
              <div className={styles.backupFileNeeded}>with backup files</div>
            )}
          </InfoSectionItem>
          <InfoSectionItem label="Supplemental">
            {posting.supplementalSoftware}
          </InfoSectionItem>
          <InfoSectionItem label="Pros required">
            {posting.capacity}
          </InfoSectionItem>
          <InfoSectionItem label="Requirements">
            {posting.specialties.join(', ')}
          </InfoSectionItem>
          <InfoSectionItem label="Type of work">
            {posting.jobTypes.map((j) => capitalizeFirstLetter(j)).join(', ')}
          </InfoSectionItem>
          <InfoSectionItem label="Description">
            {posting.description}
          </InfoSectionItem>
          <InfoSectionItem label="Status">
            <StatusText workspacePosting={posting} />
          </InfoSectionItem>
        </InfoSectionItems>
      </div>
    )
  }
)

const StaffingDetailsApplicantsSection = observer(
  function StaffingDetailsApplicantsSection({ vm }: StaffingDetailsPanelProps) {
    const contracted = !vm.applicantsExpanded
    const posting = vm.workspacePosting

    if (!posting) {
      return null
    }

    const emptyState = posting.applicants.length === 0

    return (
      <div
        className={cx(
          styles.applicantSection,
          emptyState && styles.emptyState,
          contracted && styles.contracted
        )}
      >
        {emptyState ? (
          <>
            <div className={styles.emptyStateImageContainer}>
              <img
                src={require('assets/images/img_plane-message.svg')}
                alt="Paper plane delivering an envelope."
              />
            </div>
            <div className={styles.emptyStateTextContainer}>
              <h2 className={styles.emptyStateTitle}>Post the opportunity</h2>
              <p className={styles.emptyStateDescription}>
                Once the post is shared, all applicants will be listed here for
                review.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.sectionHeader}>
              <IconButton onClick={vm.toggleApplicantsExpanded}>
                <Icon
                  className={styles.sectionAccordion}
                  color="var(--grey600)"
                  name="arrow"
                  size={24}
                />
              </IconButton>
              <div className={styles.sectionTitle}>
                Applicants
                <span className={styles.sectionTitleSuffix}>
                  {` (${posting.applicants.length})`}
                </span>
              </div>
              {vm.showManageButton && (
                <div className={styles.manageButton}>
                  <Button
                    type="text"
                    onClick={vm.applicantManagementPanelVM.show}
                  >
                    Manage
                  </Button>
                </div>
              )}
            </div>
            <div className={styles.sectionContent}>
              <Table className={styles.table} stickyHeader>
                <TableHead className={styles.tableHead}>
                  <TableHeader className={styles.avatarColumn}>
                    <div className={styles.spacer} />
                  </TableHeader>
                  <TableHeader align="left">Full name</TableHeader>
                  <TableHeader align="right">Status</TableHeader>
                </TableHead>
                <TableBody className={styles.tableBody}>
                  {posting.applicants.map((item, i) => (
                    <ApplicantRow applicant={item} key={i} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    )
  }
)

const StaffingDetailsActionFooter = observer(
  function StaffingDetailsActionFooter({ vm }: StaffingDetailsPanelProps) {
    const posting = vm.workspacePosting

    if (!posting) {
      return null
    }

    return (
      <div className={styles.footer}>
        {posting.status === WorkspacePostingStatus.Draft && (
          <div className={styles.actions}>
            <Button onClick={vm.updateDraft}>Edit</Button>
            <Button type="primary" onClick={vm.publish}>
              Submit Post
            </Button>
          </div>
        )}
        {posting.status === WorkspacePostingStatus.Active && (
          <Button type="primary" onClick={vm.markInReview}>
            Review Post
          </Button>
        )}
      </div>
    )
  }
)

interface WorkspacePostingProps {
  workspacePosting: WorkspacePosting
}

const StatusText = observer(function ({
  workspacePosting,
}: WorkspacePostingProps) {
  const formattedStatus = useObserver(() => {
    switch (workspacePosting.status) {
      case WorkspacePostingStatus.Active:
        return { text: 'Active', className: styles.statusActive }
      case WorkspacePostingStatus.Cancelled:
        return { text: 'Cancelled', className: styles.statusClosed }
      case WorkspacePostingStatus.Closed:
        return { text: 'Completed', className: styles.statusClosed }
      case WorkspacePostingStatus.Draft:
        return { text: 'Draft', className: styles.statusDraft }
      case WorkspacePostingStatus.InReview:
        return { text: 'In review', className: styles.statusInReview }
      case WorkspacePostingStatus.Null:
        return { text: 'Unspecified' }
    }
  })

  return (
    <div className={cx(styles.statusCell, formattedStatus.className)}>
      {formattedStatus.text}
    </div>
  )
})

interface ApplicantProps {
  applicant: WorkspaceApplicant
}

const ApplicantRow = observer(function ApplicantRow({
  applicant,
}: ApplicantProps) {
  return (
    <TableRow className={styles.applicantRow}>
      <ImageCell image={applicant.avatar} size={24} noPadding />
      <TextCell>
        <div className={styles.applicantName}>{applicant.displayName}</div>
      </TextCell>
      <ApplicantStatusTextCell applicant={applicant} />
    </TableRow>
  )
})

const ApplicantStatusTextCell = observer(function ({
  applicant,
}: ApplicantProps) {
  const formattedStatus = useObserver(() => {
    switch (applicant.status) {
      case WorkspaceApplicantStatus.Accepted:
        return { text: 'Accepted', className: styles.applicantStatusAccepted }
      case WorkspaceApplicantStatus.Applied:
        return { text: 'Applied', className: styles.applicantStatusApplied }
      case WorkspaceApplicantStatus.Invited:
        return { text: 'Invited', className: styles.applicantStatusInvited }
      case WorkspaceApplicantStatus.Expired:
        return { text: 'Expired', className: styles.applicantStatusExpired }
      case WorkspaceApplicantStatus.Rejected:
        return { text: 'Rejected', className: styles.applicantStatusRejected }
      case WorkspaceApplicantStatus.Withdrawn:
        return { text: 'Withdrawn', className: styles.applicantStatusWithdrawn }
      case WorkspaceApplicantStatus.Null:
        return { text: 'Unspecified' }
    }
  })

  return (
    <TextCell>
      <div className={cx(styles.applicantStatus, formattedStatus.className)}>
        {formattedStatus.text}
      </div>
    </TextCell>
  )
})
